/**
 * Permission Manager
 * Shows organization members with their org role and effective WP permissions.
 * Integrates with org role system - WP permissions are derived from org role
 * by default, with per-user overrides supported.
 */

import React, { useState } from 'react';
import {
  Shield,
  Search,
  Save,
  Check,
  X,
  Settings2,
  Users,
  RotateCcw,
} from 'lucide-react';
import type { WebPresencePermission, WebPresenceRole } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

export interface TeamMember {
  user_id: number;
  user_name: string | null;
  user_email: string | null;
  org_role: string;
  system_role: string | null;
  wp_role: string;
  has_override: boolean;
  can_edit_design: boolean;
  can_edit_content: boolean;
  can_publish: boolean;
  can_delete: boolean;
  can_manage_seo: boolean;
  can_manage_forms: boolean;
  can_manage_assets: boolean;
  can_use_code_editor: boolean;
  can_manage_content_types: boolean;
}

export interface RoleMapping {
  id: string;
  system_role: string;
  wp_role: string;
  can_edit_design: boolean;
  can_edit_content: boolean;
  can_publish: boolean;
  can_delete: boolean;
  can_manage_seo: boolean;
  can_manage_forms: boolean;
  can_manage_assets: boolean;
  can_use_code_editor: boolean;
  can_manage_content_types: boolean;
}

interface PermissionManagerProps {
  teamMembers: TeamMember[];
  roleMappings: RoleMapping[];
  permissions: WebPresencePermission[];
  onSaveOverride: (userId: string, permission: Partial<WebPresencePermission>) => Promise<void>;
  onRemoveOverride: (userId: string) => Promise<void>;
  onUpdateRoleMapping: (systemRole: string, updates: Partial<RoleMapping>) => Promise<void>;
  onDelete?: (userId: string) => Promise<void>;
}

const WP_ROLES: { value: WebPresenceRole; label: string; description: string }[] = [
  { value: 'designer', label: 'Designer', description: 'Full design access including styles and code' },
  { value: 'content_editor', label: 'Content Editor', description: 'Edit content, manage pages and assets' },
  { value: 'publisher', label: 'Publisher', description: 'Edit and publish content' },
  { value: 'viewer', label: 'Viewer', description: 'View-only access' },
];

const ROLE_PRESETS: Record<WebPresenceRole, Record<string, boolean>> = {
  designer: {
    can_edit_design: true, can_edit_content: true, can_publish: true, can_delete: true,
    can_manage_seo: true, can_manage_forms: true, can_manage_assets: true,
    can_use_code_editor: true, can_manage_content_types: true,
  },
  content_editor: {
    can_edit_design: false, can_edit_content: true, can_publish: false, can_delete: false,
    can_manage_seo: true, can_manage_forms: false, can_manage_assets: true,
    can_use_code_editor: false, can_manage_content_types: false,
  },
  publisher: {
    can_edit_design: false, can_edit_content: true, can_publish: true, can_delete: false,
    can_manage_seo: true, can_manage_forms: true, can_manage_assets: true,
    can_use_code_editor: false, can_manage_content_types: false,
  },
  viewer: {
    can_edit_design: false, can_edit_content: false, can_publish: false, can_delete: false,
    can_manage_seo: false, can_manage_forms: false, can_manage_assets: false,
    can_use_code_editor: false, can_manage_content_types: false,
  },
};

const PERMISSION_KEYS = [
  { key: 'can_edit_design', label: 'Design' },
  { key: 'can_edit_content', label: 'Content' },
  { key: 'can_publish', label: 'Publish' },
  { key: 'can_delete', label: 'Delete' },
  { key: 'can_manage_seo', label: 'SEO' },
  { key: 'can_manage_forms', label: 'Forms' },
  { key: 'can_manage_assets', label: 'Assets' },
  { key: 'can_use_code_editor', label: 'Code' },
  { key: 'can_manage_content_types', label: 'CMS' },
] as const;

const ORG_ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  program_manager: 'Program Mgr',
  manager: 'Manager',
  supervisor: 'Supervisor',
  staff: 'Staff',
  viewer: 'Viewer',
};

const WP_ROLE_COLORS: Record<string, string> = {
  designer: 'bg-purple-100 text-purple-700',
  publisher: 'bg-green-100 text-green-700',
  content_editor: 'bg-blue-100 text-blue-700',
  viewer: 'bg-gray-100 text-gray-600',
};

// ============================================================================
// ROLE MAPPING CONFIG PANEL
// ============================================================================

function RoleMappingConfig({
  roleMappings,
  onUpdate,
}: {
  roleMappings: RoleMapping[];
  onUpdate: (systemRole: string, updates: Partial<RoleMapping>) => Promise<void>;
}) {
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<RoleMapping>>({});
  const [saving, setSaving] = useState(false);

  const startEdit = (mapping: RoleMapping) => {
    setEditingRole(mapping.system_role);
    setEditData({ ...mapping });
  };

  const handleSave = async () => {
    if (!editingRole) return;
    setSaving(true);
    try {
      await onUpdate(editingRole, editData);
    } finally {
      setSaving(false);
      setEditingRole(null);
      setEditData({});
    }
  };

  const applyPreset = (wpRole: WebPresenceRole) => {
    setEditData(prev => ({
      ...prev,
      wp_role: wpRole,
      ...ROLE_PRESETS[wpRole],
    }));
  };

  return (
    <div className="border rounded-lg bg-gray-50 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Settings2 className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-700">Default Role Mappings</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Configure which Web Presence role each organization role maps to by default.
        Individual users can be overridden below.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left text-xs font-medium text-gray-500 py-2 pr-4">Org Role</th>
            <th className="text-left text-xs font-medium text-gray-500 py-2 pr-4">WP Role</th>
            {PERMISSION_KEYS.map(p => (
              <th key={p.key} className="text-center text-xs font-medium text-gray-500 py-2 px-1">{p.label}</th>
            ))}
            <th className="w-16"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {roleMappings.map(mapping => {
            const isEditing = editingRole === mapping.system_role;
            const data = isEditing ? editData : mapping;

            return (
              <tr key={mapping.system_role} className={isEditing ? 'bg-blue-50' : ''}>
                <td className="py-2 pr-4">
                  <span className="text-xs font-medium">{ORG_ROLE_LABELS[mapping.system_role] || mapping.system_role}</span>
                </td>
                <td className="py-2 pr-4">
                  {isEditing ? (
                    <select
                      value={data.wp_role || 'viewer'}
                      onChange={(e) => applyPreset(e.target.value as WebPresenceRole)}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {WP_ROLES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${WP_ROLE_COLORS[data.wp_role || 'viewer'] || 'bg-gray-100 text-gray-600'}`}>
                      {WP_ROLES.find(r => r.value === data.wp_role)?.label || data.wp_role}
                    </span>
                  )}
                </td>
                {PERMISSION_KEYS.map(p => (
                  <td key={p.key} className="text-center py-2 px-1">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={!!(data as any)[p.key]}
                        onChange={() => setEditData(prev => ({ ...prev, [p.key]: !(prev as any)[p.key] }))}
                        className="rounded h-3 w-3"
                      />
                    ) : (
                      (data as any)[p.key] ? (
                        <Check className="h-3 w-3 text-green-500 mx-auto" />
                      ) : (
                        <X className="h-3 w-3 text-gray-300 mx-auto" />
                      )
                    )}
                  </td>
                ))}
                <td className="py-2 text-right">
                  {isEditing ? (
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={handleSave} disabled={saving} className="p-1 hover:bg-green-100 rounded text-green-600">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => { setEditingRole(null); setEditData({}); }} className="p-1 hover:bg-gray-200 rounded">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => startEdit(mapping)} className="text-xs text-blue-600 hover:text-blue-700">
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PermissionManager({
  teamMembers,
  roleMappings,
  permissions,
  onSaveOverride,
  onRemoveOverride,
  onUpdateRoleMapping,
}: PermissionManagerProps) {
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showMappings, setShowMappings] = useState(false);

  const filteredMembers = searchQuery
    ? teamMembers.filter(m =>
        (m.user_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.user_email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.org_role.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : teamMembers;

  const startEditing = (member: TeamMember) => {
    setEditingUserId(member.user_id);
    setEditData({
      wp_role: member.wp_role,
      can_edit_design: member.can_edit_design,
      can_edit_content: member.can_edit_content,
      can_publish: member.can_publish,
      can_delete: member.can_delete,
      can_manage_seo: member.can_manage_seo,
      can_manage_forms: member.can_manage_forms,
      can_manage_assets: member.can_manage_assets,
      can_use_code_editor: member.can_use_code_editor,
      can_manage_content_types: member.can_manage_content_types,
    });
  };

  const applyPreset = (wpRole: WebPresenceRole) => {
    setEditData(prev => ({
      ...prev,
      wp_role: wpRole,
      ...ROLE_PRESETS[wpRole],
    }));
  };

  const handleSave = async () => {
    if (editingUserId === null) return;
    await onSaveOverride(String(editingUserId), {
      userId: String(editingUserId),
      role: editData.wp_role,
      canEditDesign: editData.can_edit_design,
      canEditContent: editData.can_edit_content,
      canPublish: editData.can_publish,
      canDelete: editData.can_delete,
      canManageSeo: editData.can_manage_seo,
      canManageForms: editData.can_manage_forms,
      canManageAssets: editData.can_manage_assets,
      canUseCodeEditor: editData.can_use_code_editor,
      canManageContentTypes: editData.can_manage_content_types,
    } as any);
    setEditingUserId(null);
    setEditData({});
  };

  const handleResetToDefault = async (userId: number) => {
    await onRemoveOverride(String(userId));
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold">Team Permissions</h2>
          <span className="text-sm text-gray-500">
            {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          onClick={() => setShowMappings(!showMappings)}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors ${
            showMappings ? 'bg-blue-50 border-blue-200 text-blue-700' : 'hover:bg-gray-50'
          }`}
        >
          <Settings2 className="h-3.5 w-3.5" />
          Role Defaults
        </button>
      </div>

      {/* Role Mapping Config (collapsible) */}
      {showMappings && roleMappings.length > 0 && (
        <div className="px-4 py-3 border-b bg-gray-50/50">
          <RoleMappingConfig
            roleMappings={roleMappings}
            onUpdate={onUpdateRoleMapping}
          />
        </div>
      )}

      {/* Search */}
      <div className="border-b px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or role..."
            className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Team Members Table */}
      <div className="flex-1 overflow-auto">
        {filteredMembers.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {teamMembers.length === 0 ? 'No Team Members Found' : 'No Matches'}
            </h3>
            <p className="text-gray-500 text-sm">
              {teamMembers.length === 0
                ? 'Organization members will appear here with their web presence permissions.'
                : 'Try a different search term.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b sticky top-0">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">User</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-3">Org Role</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-3">WP Role</th>
                {PERMISSION_KEYS.map(p => (
                  <th key={p.key} className="text-center text-xs font-medium text-gray-500 uppercase px-1 py-3">
                    {p.label}
                  </th>
                ))}
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredMembers.map((member) => {
                const isEditing = editingUserId === member.user_id;
                const data = isEditing ? editData : member;

                return (
                  <tr key={member.user_id} className={isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    {/* User info */}
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-sm font-medium text-gray-900 block">
                          {member.user_name || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-500">{member.user_email}</span>
                      </div>
                    </td>

                    {/* Org role */}
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                        {ORG_ROLE_LABELS[member.org_role] || member.org_role}
                      </span>
                    </td>

                    {/* WP role */}
                    <td className="px-3 py-3">
                      {isEditing ? (
                        <select
                          value={data.wp_role || 'viewer'}
                          onChange={(e) => applyPreset(e.target.value as WebPresenceRole)}
                          className="border rounded px-2 py-1 text-xs"
                        >
                          {WP_ROLES.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${WP_ROLE_COLORS[data.wp_role || 'viewer'] || 'bg-gray-100 text-gray-600'}`}>
                            {WP_ROLES.find(r => r.value === data.wp_role)?.label || data.wp_role}
                          </span>
                          {member.has_override && (
                            <span className="text-xs text-orange-500" title="Custom override - differs from org role default">
                              *
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Permission checkboxes */}
                    {PERMISSION_KEYS.map(p => (
                      <td key={p.key} className="text-center px-1 py-3">
                        {isEditing ? (
                          <input
                            type="checkbox"
                            checked={!!data[p.key]}
                            onChange={() => setEditData(prev => ({ ...prev, [p.key]: !prev[p.key] }))}
                            className="rounded h-3.5 w-3.5"
                          />
                        ) : (
                          (data as any)[p.key] ? (
                            <Check className="h-3.5 w-3.5 text-green-500 mx-auto" />
                          ) : (
                            <X className="h-3.5 w-3.5 text-gray-300 mx-auto" />
                          )
                        )}
                      </td>
                    ))}

                    {/* Actions */}
                    <td className="px-3 py-3">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <button onClick={handleSave} className="p-1 hover:bg-green-100 rounded text-green-600" title="Save">
                            <Save className="h-4 w-4" />
                          </button>
                          <button onClick={() => { setEditingUserId(null); setEditData({}); }} className="p-1 hover:bg-gray-200 rounded" title="Cancel">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEditing(member)} className="text-xs text-blue-600 hover:text-blue-700">
                            Override
                          </button>
                          {member.has_override && (
                            <button
                              onClick={() => handleResetToDefault(member.user_id)}
                              className="p-1 hover:bg-gray-200 rounded text-gray-500"
                              title="Reset to org role default"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default PermissionManager;
